import { useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { acceptInvite, AuthError, passwordProblem } from '../api/auth'
import { useSetSession } from '../useSession'
import AuthLayout, { FormError, PasswordFields, SubmitButton } from './AuthLayout'
import { landingFor } from '../landing'
import { navSubjectFrom } from '../navSubject'

export default function AcceptInviteView() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<AuthError | null>(null)
  const [busy, setBusy] = useState(false)
  const [attempted, setAttempted] = useState(false)

  const navigate = useNavigate()
  const setSession = useSetSession()

  // A link with no token cannot be recovered from by trying, so the form is
  // never shown — filling it in and then failing wastes the person's time.
  if (!token) {
    return (
      <AuthLayout
        title="This link is incomplete"
        subtitle="The invitation link is missing its code. It may have been cut short by an email client."
      >
        <FormError
          title="Nothing to accept"
          detail="Ask an administrator to send a fresh invitation, and open the link directly rather than copying it."
        />
      </AuthLayout>
    )
  }

  const problem = passwordProblem(password, confirm)

  async function submit(event: FormEvent) {
    event.preventDefault()

    // Never a silent return (D-186). Marking the attempt is what puts the
    // reason on screen — the previous version simply did nothing.
    setAttempted(true)
    if (problem) return

    setBusy(true)
    setError(null)

    try {
      const session = await acceptInvite(token, password)
      setSession(session)

      /*
       * <b>Where she belongs, not where the calendar is</b> (D-608, applying
       * D-176 and D-370).
       *
       * Accepting signs you in server-side, so going to the login screen here
       * would ask for a password chosen thirty seconds ago — that part was
       * always right. The destination was not: `/calendar` needs
       * `ViewAllSchedules`, which no parent, teacher or dancer holds, so
       * **every pilot family setting a password landed on "Not your page"** as
       * the first screen they ever saw. Found on production the night before
       * the pilot, by the owner, on his own new parent account.
       *
       * <b>One function already answers this</b> and three other doors ask it:
       * sign-in, the home address, and the rail. This was the fourth door,
       * asking nobody. That is the drift A-17 names — a second answer to one
       * question, and the one that disagreed was the one a new family met
       * first.
       */
      navigate(landingFor(navSubjectFrom(session)), { replace: true })
    } catch (e) {
      setError(
        e instanceof AuthError
          ? e
          : new AuthError('Could not reach the studio system. Check your connection.', 0),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      title="Choose a password"
      subtitle="This is the last step — pick a password and you will be signed in."
    >
      {error && <FormError title={error.message} detail={error.detail} />}

      <form onSubmit={submit} noValidate>
        <PasswordFields
          password={password}
          confirm={confirm}
          onPassword={setPassword}
          onConfirm={setConfirm}
          problem={problem}
          showProblem={attempted}
        />

        <SubmitButton busy={busy}>Set password and continue</SubmitButton>
      </form>
    </AuthLayout>
  )
}
